import type { SectionTimestampEntry, RoomStatus } from '../../lib/rooms';
import type { Template } from '../../lib/templates';
import { ExportMenu } from './ExportMenu';

interface StatusBandProps {
  status: RoomStatus;
  roomId: number;
  template: Template;
  streamStartedAt: string | null;
  sectionTimestamps: SectionTimestampEntry[];
}

/**
 * The hairline + eyebrow band `docs/DESIGN.md` calls for above the table once a room has left
 * LIVE: lime for FINISHED (carrying a compact `ExportMenu` per `docs/specs/WP8-exports.md`), struck
 * /faint for ARCHIVED. Renders nothing for DRAFT/LIVE — those statuses have no band, just the
 * `StatusPill` already in the room header.
 */
export function StatusBand({ status, roomId, template, streamStartedAt, sectionTimestamps }: StatusBandProps) {
  if (status === 'FINISHED') {
    return (
      <div className="reveal mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3" role="status">
        <div className="flex items-baseline gap-4">
          <span className="eyebrow text-accent">Finished</span>
          <span className="telemetry text-text-faint">Read-only for guests &amp; co-hosts</span>
        </div>
        <span className="h-px w-full flex-1 bg-accent-border sm:w-auto" aria-hidden="true" />
        <ExportMenu
          roomId={roomId}
          template={template}
          streamStartedAt={streamStartedAt}
          sectionTimestamps={sectionTimestamps}
          compact
        />
      </div>
    );
  }
  if (status === 'ARCHIVED') {
    return (
      <div className="reveal mb-6 flex items-baseline gap-4" role="status">
        <span className="eyebrow text-text-faint line-through">Archived</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
        <span className="telemetry text-text-faint">Off the Home list — still reachable by this URL</span>
      </div>
    );
  }
  return null;
}
